import React from "react";
import DesktopHeader from "../../components/DesktopHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import DesktopBody from "../../components/DesktopBody";
import TableComponent from "../../components/TableComponent";
import { TableData } from "../../components/TableComponent/TableComponent";
import api from "../../api";
import dayjs, { Dayjs } from "dayjs";
import { CircularProgress, Tooltip } from "@mui/material";
import {
  DesktopFiltersProps,
  FilterData,
} from "../../components/DesktopFilters/DesktopFilters";
import {
  selectHasUserPropertySetting,
  selectUser,
  selectUserRole,
} from "../../slices/user";
import DesktopDropView from "../../components/DesktopDropView";
import NewInvitationForm from "../../components/NewInvitationForm";
import DesktopInvitationDetails from "../../components/DesktopInvitationDetails";
import { selectToken } from "../../slices/token";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import IconifyIcon from "../../components/IconifyIcon";
import { Context } from "../../context/ContextProvider/ContextProvider";
import { AccessKeyCreateResponse } from "../../api/models/access-key-model";
import { showError } from "../../utils/helpers";
import { CSVLink } from "react-csv";
import DeleteAccessKeyModal from "../../components/Modals/DeleteAccessKeyModal";
import UserQrCode from "../../components/UserQrCode";
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const AccessKeys = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const token = useAppSelector(selectToken);
  const userRole = useAppSelector(selectUserRole);
  const hasSecUserPerms = useAppSelector(
    selectHasUserPropertySetting("sec_user_perms")
  );
  const isGuard = userRole === "guard";
  const dispatch = useAppDispatch();

  const isSecondaryHostRestricted =
    userRole === "secondary_host" && !hasSecUserPerms;

  const propertyId = user?.property?.id;
  const unitId = user?.unit?.id;

  const { selectedAccessPointGuard } = React.useContext(Context);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [tableDataToDisplay, setTableDataToDisplay] =
    React.useState<TableData | null>(null);
  const [searchFilter, setSearchFilter] = React.useState<string>("");
  const [selectedAccessPoint, setSelectedAccessPoint] =
    React.useState<string>("0");
  const [selectedState, setSelectedState] = React.useState<
    "" | "access" | "exit" | "try" | "deleted" | "unused"
  >("");
  const [selectedDateFilter, setSelectedDateFilter] =
    React.useState<string>("all_dates");
  const [showAccessKeyDetails, setShowAccessKeyDetails] =
    React.useState<boolean>(false);
  const [newInvitation, setNewInvitation] = React.useState<boolean>(false);
  const [dateFilter, setDateFilter] = React.useState<Dayjs | null>(null);
  const [validFrom, setValidFrom] = React.useState<Dayjs | null>(null);
  const [validUntil, setValidUntil] = React.useState<Dayjs | null>(null);
  const [selectedInvitationId, setSelectedInvitationId] = React.useState<
    string | null
  >(null);
  const [selectedInvitation, setSelectedInvitation] =
    React.useState<AccessKeyCreateResponse | null>(null);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [totalInvitations, setTotalInvitations] = React.useState<number>(0);
  const [showContacts, setShowContacts] = React.useState<boolean>(false);
  const [verifiableIds, setVerifiableIds] = React.useState<
    { accessKeyId: number; verifiableId: string }[]
  >([]);
  const [accessKeys, setAccessKeys] = React.useState<AccessKeyCreateResponse[]>(
    []
  );
  const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);
  const [editingAccessKey, setEditingAccessKey] =
    React.useState<AccessKeyCreateResponse | null>(null);
  const [csvExportData, setCsvExportData] = React.useState<
    Record<string, string>[]
  >([]);
  const [isExporting, setIsExporting] = React.useState(false);
  const csvLinkRef = React.useRef<{ link?: HTMLAnchorElement } | null>(null);

  const debouncedSearchFilter = useDebounce(searchFilter, 300);

  const isoDate = dateFilter ? dayjs(dateFilter).toISOString() : "";
  const validFromIsoDate = validFrom ? dayjs(validFrom).toISOString() : "";
  const validUntilIsoDate = validUntil ? dayjs(validUntil).toISOString() : "";

  const disabledWhatsAppByOneTimeVisitor = selectedInvitation?.one_time_visitor
    ?.phone
    ? selectedInvitation?.one_time_visitor?.phone === "+52"
      ? true
      : selectedInvitation?.one_time_visitor?.phone === "+1"
      ? true
      : false
    : false;
  const disabledWhatsAppByVerifiedVisitor = selectedInvitation?.verified_visitor
    ?.phone
    ? selectedInvitation?.verified_visitor?.phone === "+52"
      ? true
      : selectedInvitation?.verified_visitor?.phone === "+1"
      ? true
      : false
    : false;

  const canEditSelectedAccessKey =
    !!selectedInvitation &&
    !!user &&
    selectedInvitation?.created?.id === user?.id;

  const handleEditAccessKey = () => {
    if (
      !selectedInvitation ||
      selectedInvitation?.daleted_at ||
      !selectedInvitation?.verifiable_ids[0]?.uuid ||
      !canEditSelectedAccessKey
    ) {
      return;
    }
    setEditingAccessKey(selectedInvitation);
    setShowAccessKeyDetails(false);
    setNewInvitation(true);
  };

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
        return {
          label: item?.name,
          value: `${item.id}`,
        };
      })
    : [];

  const datesFilterArray = [
    {
      label: labels.all_dates,
      value: "all_dates",
    },
    {
      label: labels.today,
      value: "today",
    },
    {
      label: labels.last_week,
      value: "last_week",
    },
    {
      label: labels.last_month,
      value: "last_month",
    },
    {
      label: labels.calendar,
      value: "calendar",
    },
  ];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const handleDownloadCsv = React.useCallback(async () => {
    if (!token || !propertyId) return;
    let currentValidFrom = undefined;
    let currentValidTo = undefined;
    let currentDate = undefined;
    let currentState;
    let currentAccessPoint;
    if (selectedState === "") {
      currentState = undefined;
    } else {
      currentState = selectedState;
    }
    if (!isGuard) {
      currentAccessPoint =
        selectedAccessPoint === "0"
          ? undefined
          : Number(selectedAccessPoint);
    } else {
      currentAccessPoint = selectedAccessPointGuard
        ? Number(selectedAccessPointGuard)
        : undefined;
    }
    if (validFromIsoDate && validUntilIsoDate) {
      currentValidFrom = validFromIsoDate;
      currentValidTo = validUntilIsoDate;
    }
    if (isoDate) {
      currentDate = isoDate;
    }
    try {
      setIsExporting(true);
      const response = await api.accessKeysByPropertyRequest(
        Number(propertyId),
        1,
        10000,
        undefined,
        currentState,
        currentAccessPoint,
        currentValidFrom,
        currentValidTo,
        currentDate,
        debouncedSearchFilter,
        userRole === "host" ? (unitId ?? -1) : undefined,
        userRole === "secondary_host" ? (user?.id ?? -1) : undefined,
      );
      if (!response?.data?.length) {
        setCsvExportData([]);
        setIsExporting(false);
        return;
      }
      const csvRows: Record<string, string>[] = response.data.map((item) => {
        const isVerifiedVisitor = item?.verified_visitor?.name || null;
        const isOtherProvider =
          item?.one_time_visitor?.provider_name || null;
        const isProvider = item?.one_time_visitor?.provider?.name || null;
        const isOneTimeVisitor = item?.one_time_visitor?.name || null;
        const invName = isVerifiedVisitor
          ? isVerifiedVisitor
          : isOtherProvider
            ? isOtherProvider
            : isProvider
              ? isProvider
              : isOneTimeVisitor
                ? isOneTimeVisitor
                : "";
        const typeLabel =
          item?.access_key_type === "provider"
            ? labels.provider
            : item?.access_key_type === "frequent"
              ? labels.recurrent
              : item?.access_key_type === "authorization"
                ? labels.authorization_pass
                : labels.visitor;
        const formatValidDate = (d: string | undefined) => {
          if (!dayjs(d).isValid()) return labels.invalid_date;
          const m = dayjs(d).format("MMMM");
          return (
            dayjs(d).format("DD") +
            "/" +
            m.charAt(0).toUpperCase() +
            m.slice(1)
          );
        };
        const lastTransactionType = item?.last_transaction_type || null;
        const stateLabel = item?.daleted_at
          ? labels.deleted_one
          : lastTransactionType === null
            ? labels.unused_one
            : lastTransactionType?.type === "entrance"
              ? labels.entrance
              : lastTransactionType?.type === "exit"
                ? labels.exit
                : lastTransactionType?.type === "try"
                  ? labels.not_authorized_ak_one
                  : "";
        return {
          [labels.visitor]: invName,
          [labels.type]: typeLabel,
          [labels.user]: item?.created?.name ?? "",
          [labels.unit]: item?.unit?.name ?? "",
          [labels.valid_from]: formatValidDate(item?.valid_from),
          [labels.valid_until]: formatValidDate(item?.valid_until),
          [labels.state]: stateLabel,
        };
      });
      setCsvExportData(csvRows);
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(
        dispatch,
        errorMessage ? errorMessage : labels.generic_error
      );
      setIsExporting(false);
    }
  }, [
    token,
    propertyId,
    selectedState,
    isGuard,
    validFromIsoDate,
    validUntilIsoDate,
    isoDate,
    selectedAccessPoint,
    selectedAccessPointGuard,
    debouncedSearchFilter,
    userRole,
    unitId,
    labels.visitor,
    labels.type,
    labels.user,
    labels.unit,
    labels.valid_from,
    labels.valid_until,
    labels.state,
    labels.provider,
    labels.recurrent,
    labels.authorization_pass,
    labels.invalid_date,
    labels.deleted_one,
    labels.unused_one,
    labels.entrance,
    labels.exit,
    labels.not_authorized_ak_one,
    labels.generic_error,
    dispatch,
  ]);

  React.useEffect(() => {
    if (csvExportData.length === 0) return;
    const timer = setTimeout(() => {
      const link = csvLinkRef.current?.link;
      if (link) {
        link.click();
      }
      setCsvExportData([]);
      setIsExporting(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [csvExportData]);

  const filters: DesktopFiltersProps = !isGuard
    ? {
        filterData: [
          {
            type: "multidate",
            value: selectedDateFilter,
            setValue: setSelectedDateFilter,
            disabled: isLoading,
            tooltip: "",
            function: () => {},
            array: datesFilterArray,
            size: "small",
            icon: "",
            label: labels.access_points,
            multiDateValue: dateFilter,
            setMultiDateValue: setDateFilter,
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.entries,
            function: () => {},
            array: [],
            size: "small",
            icon: "entypo:login",
            label: "access",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.exits,
            function: () => {},
            array: [],
            size: "small",
            icon: "entypo:log-out",
            label: "exit",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.not_authorized_ak,
            function: () => {},
            array: [],
            size: "small",
            icon: "entypo:block",
            label: "try",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.unused,
            function: () => {},
            array: [],
            size: "small",
            icon: "fa-solid:qrcode",
            label: "unused",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.deleted,
            function: () => {},
            array: [],
            size: "small",
            icon: "f7:trash-fill",
            label: "deleted",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "search",
            value: searchFilter,
            setValue: setSearchFilter,
            disabled: isLoading,
            tooltip: "",
            function: () => {},
            array: [],
            size: "small",
            icon: "",
            label: "",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "function",
            value: "",
            setValue: () => {},
            disabled: isLoading || isExporting,
            tooltip: labels.download_content,
            function: handleDownloadCsv,
            array: [],
            size: "small",
            icon: "fa-solid:file-download",
            label: "",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "function",
            value: "",
            setValue: () => {},
            disabled: isLoading,
            tooltip: "",
            function: () => setNewInvitation(true),
            array: [],
            size: "small",
            icon: "fa6-solid:circle-plus",
            label: "",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
        ],
      }
    : {
        filterData: [
          {
            type: "multidate",
            value: selectedDateFilter,
            setValue: setSelectedDateFilter,
            disabled: isLoading,
            tooltip: "",
            function: () => {},
            array: datesFilterArray,
            size: "small",
            icon: "",
            label: labels.access_points,
            multiDateValue: dateFilter,
            setMultiDateValue: setDateFilter,
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.entries,
            function: () => {},
            array: [],
            size: "small",
            icon: "entypo:login",
            label: "access",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.exits,
            function: () => {},
            array: [],
            size: "small",
            icon: "entypo:log-out",
            label: "exit",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.not_authorized_ak,
            function: () => {},
            array: [],
            size: "small",
            icon: "entypo:block",
            label: "try",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.unused,
            function: () => {},
            array: [],
            size: "small",
            icon: "fa-solid:qrcode",
            label: "unused",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "icon",
            value: selectedState,
            setValue: setSelectedState,
            disabled: isLoading,
            tooltip: labels.deleted,
            function: () => {},
            array: [],
            size: "small",
            icon: "f7:trash-fill",
            label: "deleted",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "search",
            value: searchFilter,
            setValue: setSearchFilter,
            disabled: isLoading,
            tooltip: "",
            function: () => {},
            array: [],
            size: "small",
            icon: "",
            label: "",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
          {
            type: "function",
            value: "",
            setValue: () => {},
            disabled: isLoading || isExporting,
            tooltip: labels.download_content,
            function: handleDownloadCsv,
            array: [],
            size: "small",
            icon: "fa-solid:file-download",
            label: "",
            multiDateValue: null,
            setMultiDateValue: () => {},
          },
        ],
      };

  if (!isGuard) {
    const elementToAdd: FilterData = {
      type: "selector",
      value: selectedAccessPoint,
      setValue: setSelectedAccessPoint,
      disabled: isLoading,
      tooltip: "",
      function: () => {},
      array: accessPointsArray,
      size: "small",
      icon: "",
      label: labels.access_points,
      multiDateValue: null,
      setMultiDateValue: () => {},
    };

    filters.filterData?.splice(1, 0, elementToAdd);
  }

  React.useEffect(() => {
    setPage(0);
  }, [
    selectedDateFilter,
    validFrom,
    validUntil,
    isoDate,
    selectedState,
    selectedAccessPoint,
    selectedAccessPointGuard,
    debouncedSearchFilter,
  ]);

  const getAccessKeys = React.useCallback(
    async (debouncedText: string) => {
      if (token && propertyId) {
        let currentValidFrom = undefined;
        let currentValidTo = undefined;
        let currentDate = undefined;
        let currentState;
        let currentAccessPoint;
        if (selectedState === "") {
          currentState = undefined;
        } else {
          currentState = selectedState;
        }
        if (!isGuard) {
          if (selectedAccessPoint === "0") {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPoint);
          }
        } else {
          if (!selectedAccessPointGuard) {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPointGuard);
          }
        }
        if (validFromIsoDate && validUntilIsoDate) {
          currentValidFrom = validFromIsoDate;
          currentValidTo = validUntilIsoDate;
        }
        if (isoDate) {
          currentDate = isoDate;
        }
        try {
          setIsLoading(true);
          //TODO: APLICAR FILTROS FECHA Y RANGO DE FECHAS
          const response = await api.accessKeysByPropertyRequest(
            Number(propertyId),
            page + 1,
            rowsPerPage,
            undefined,
            currentState,
            currentAccessPoint,
            currentValidFrom,
            currentValidTo,
            currentDate,
            debouncedText,
            userRole === "host" ? (unitId ?? -1) : undefined,
            userRole === "secondary_host" ? (user?.id ?? -1) : undefined,
          );
          if (response) {
            setAccessKeys(response?.data);
            const totalRows = response?.meta?.pagination?.total;
            setTotalInvitations(totalRows);
            const dataForTable: TableData = {
              header: [
                {
                  label: labels.visitor,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.type,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.user,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.unit,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.valid_from,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.valid_until,
                  isIcon: false,
                  tooltipLabel: "",
                },
                {
                  label: labels.state,
                  isIcon: false,
                  tooltipLabel: "",
                },
              ],
              body: response?.data?.map((item) => {
                const isVerifiedVisitor = item?.verified_visitor?.name || null;
                const isOtherProvider =
                  item?.one_time_visitor?.provider_name || null;
                const isProvider =
                  item?.one_time_visitor?.provider?.name || null;
                const isOneTimeVisitor = item?.one_time_visitor?.name || null;
                const InvName = isVerifiedVisitor
                  ? isVerifiedVisitor
                  : isOtherProvider
                  ? isOtherProvider
                  : isProvider
                  ? isProvider
                  : isOneTimeVisitor
                  ? isOneTimeVisitor
                  : "";
                const lastTransactionType = item?.last_transaction_type || null;
                return {
                  row: item?.id,
                  cells: [
                    {
                      label: [
                        {
                          mainLabel: InvName,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel:
                            item?.access_key_type === "provider"
                              ? labels.provider
                              : item?.access_key_type === "frequent"
                              ? labels.recurrent
                              : item?.access_key_type === "authorization"
                              ? labels.authorization_pass
                              : labels.visitor,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel: item?.created?.name,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-row",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel: item?.unit?.name,
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      display: "flex-dir-col",
                      isButton: false,
                    },
                    {
                      label: dayjs(item?.valid_from).isValid()
                        ? [
                            {
                              mainLabel:
                                dayjs(item?.valid_from).format("DD") +
                                "/" +
                                dayjs(item?.valid_from)
                                  .format("MMMM")
                                  .charAt(0)
                                  .toUpperCase() +
                                dayjs(item?.valid_from).format("MMMM").slice(1),
                              tooltipLabel: "",
                            },
                          ]
                        : [
                            {
                              mainLabel: labels.invalid_date,
                              tooltipLabel: "",
                            },
                          ],
                      isIcon: false,
                      display: "flex-dir-col",
                      isButton: false,
                    },
                    {
                      label: dayjs(item?.valid_until).isValid()
                        ? [
                            {
                              mainLabel:
                                dayjs(item?.valid_until).format("DD") +
                                "/" +
                                dayjs(item?.valid_until)
                                  .format("MMMM")
                                  .charAt(0)
                                  .toUpperCase() +
                                dayjs(item?.valid_until)
                                  .format("MMMM")
                                  .slice(1),
                              tooltipLabel: "",
                            },
                          ]
                        : [
                            {
                              mainLabel: labels.invalid_date,
                              tooltipLabel: "",
                            },
                          ],
                      isIcon: false,
                      display: "flex-dir-col",
                      isButton: false,
                    },
                    {
                      label: [
                        {
                          mainLabel: item?.daleted_at
                            ? labels.deleted_one
                            : lastTransactionType === null
                            ? labels.unused_one
                            : lastTransactionType?.type === "entrance"
                            ? labels.entrance
                            : lastTransactionType?.type === "exit"
                            ? labels.exit
                            : lastTransactionType?.type === "try"
                            ? labels.not_authorized_ak_one
                            : "",
                          tooltipLabel: "",
                        },
                      ],
                      isIcon: false,
                      isBadge: true,
                      color: item?.daleted_at
                        ? "#b91c1c"
                        : lastTransactionType === null
                        ? "#334155"
                        : lastTransactionType?.type === "entrance"
                        ? "#166534"
                        : lastTransactionType?.type === "exit"
                        ? "#0f766e"
                        : lastTransactionType?.type === "try"
                        ? "#b45309"
                        : "#334155",
                      backgroundColor: item?.daleted_at
                        ? "#fee2e2"
                        : lastTransactionType === null
                        ? "#e2e8f0"
                        : lastTransactionType?.type === "entrance"
                        ? "#dcfce7"
                        : lastTransactionType?.type === "exit"
                        ? "#ccfbf1"
                        : lastTransactionType?.type === "try"
                        ? "#ffedd5"
                        : "#e2e8f0",
                      display: "flex-dir-row",
                      isButton: false,
                    },
                  ],
                };
              }),
            };
            setTableDataToDisplay(dataForTable);
            const verifiableIdsArray = response?.data.map((item) => {
              return {
                accessKeyId: item?.id,
                verifiableId: item?.verifiable_ids[0]?.documentId,
              };
            });
            setVerifiableIds(verifiableIdsArray);
          }
        } catch (error: any) {
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error
          );
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      token,
      propertyId,
      selectedState,
      isGuard,
      validFromIsoDate,
      validUntilIsoDate,
      isoDate,
      selectedAccessPoint,
      selectedAccessPointGuard,
      page,
      rowsPerPage,
      userRole,
      unitId,
      user?.id,
      labels.visitor,
      labels.type,
      labels.user,
      labels.unit,
      labels.valid_from,
      labels.valid_until,
      labels.state,
      labels.provider,
      labels.recurrent,
      labels.authorization_pass,
      labels.invalid_date,
      labels.deleted_one,
      labels.unused_one,
      labels.entrance,
      labels.exit,
      labels.not_authorized_ak_one,
      labels.generic_error,
      dispatch,
    ]
  );

  React.useEffect(() => {
    getAccessKeys(debouncedSearchFilter);
  }, [debouncedSearchFilter, getAccessKeys]);

  React.useEffect(() => {
    if (selectedDateFilter === "last_week") {
      const valid_from = dayjs()
        .startOf("week")
        .add(1, "day")
        .set("hour", 0)
        .set("minute", 1);
      const valid_until = dayjs()
        .endOf("week")
        .add(1, "day")
        .set("hour", 23)
        .set("minute", 59);
      setValidFrom(valid_from);
      setValidUntil(valid_until);
      setDateFilter(null);
    }
    if (selectedDateFilter === "last_month") {
      const valid_from = dayjs()
        .startOf("month")
        .set("hour", 0)
        .set("minute", 1);
      const valid_until = dayjs()
        .endOf("month")
        .set("hour", 23)
        .set("minute", 59);
      setValidFrom(valid_from);
      setValidUntil(valid_until);
      setDateFilter(null);
    }
    if (selectedDateFilter === "today") {
      const today = dayjs().startOf("day");
      setValidFrom(null);
      setValidUntil(null);
      setDateFilter(today);
    }
    if (selectedDateFilter === "all_dates") {
      setValidFrom(null);
      setValidUntil(null);
      setDateFilter(null);
    }
  }, [selectedDateFilter]);

  const sendEmail = () => {
    if (selectedInvitation) {
      const urlToCopy = `https://tenant.passtrack.mx/my-access-key/${selectedInvitation?.uuid}`;
      const user = selectedInvitation?.verified_visitor
        ? selectedInvitation?.verified_visitor
        : selectedInvitation?.one_time_visitor
        ? selectedInvitation?.one_time_visitor
        : null;
      if (user && user?.email && selectedInvitation?.verifiable_ids[0]?.uuid) {
        const email = user?.email;
        const subject = labels.invitation;
        const body = urlToCopy;
        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
      }
    }
  };

  const sendWhatsApp = () => {
    if (
      selectedInvitation &&
      selectedInvitation?.verifiable_ids[0]?.uuid &&
      (selectedInvitation?.one_time_visitor?.phone ||
        selectedInvitation?.verified_visitor?.phone)
    ) {
      const phoneToUse = selectedInvitation?.verified_visitor?.phone
        ? selectedInvitation?.verified_visitor?.phone
        : selectedInvitation?.one_time_visitor?.phone
        ? selectedInvitation?.one_time_visitor?.phone
        : "";
      const phoneNumber = phoneToUse;
      const urlToCopy = `https://tenant.passtrack.mx/my-access-key/${selectedInvitation?.uuid}`;
      const message = urlToCopy;
      const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
        message
      )}`;
      window.open(whatsappLink, "_blank");
    }
  };

  const copyToClipboard = () => {
    if (selectedInvitation && selectedInvitation?.verifiable_ids[0]?.uuid) {
      const urlToCopy = `https://tenant.passtrack.mx/my-access-key/${selectedInvitation?.uuid}`;
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(urlToCopy)
          .then(() => {
            alert(labels.invitation_copy);
          })
          .catch((err) => {
            console.error("Error al copiar al portapapeles:", err);
          });
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = urlToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        alert(labels.invitation_copy);
      }
    }
  };

  const deleteAccessKey = async () => {
    if (selectedInvitation) {
      try {
        const response = await api.updateAccessKeyRequest(
          selectedInvitation.id,
          { daleted_at: dayjs().toISOString() }
        );
        if (response) {
          setOpenDeleteModal(false);
          setShowAccessKeyDetails(false);
          getAccessKeys(debouncedSearchFilter);
        }
      } catch (error: any) {
        console.error("Error al copiar al portapapeles:", error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      }
    }
  };

  React.useEffect(() => {
    if (!showAccessKeyDetails && selectedInvitationId) {
      setSelectedInvitationId(null);
      setSelectedInvitation(null);
    }
  }, [selectedInvitationId, showAccessKeyDetails]);

  const csvHeaders = [
    { label: labels.visitor, key: labels.visitor },
    { label: labels.type, key: labels.type },
    { label: labels.user, key: labels.user },
    { label: labels.unit, key: labels.unit },
    { label: labels.valid_from, key: labels.valid_from },
    { label: labels.valid_until, key: labels.valid_until },
    { label: labels.state, key: labels.state },
  ];

  return (
    <div className="w-100">
      {csvExportData.length > 0 && (
        <CSVLink
          ref={csvLinkRef as any}
          data={csvExportData}
          headers={csvHeaders}
          filename="llaves-acceso.csv"
          style={{ display: "none" }}
        />
      )}
      <DeleteAccessKeyModal
        showDeleteAccessKeyModal={openDeleteModal}
        setShowDeleteAccessKeyModal={setOpenDeleteModal}
        deleteFunction={deleteAccessKey}
        selectedAccessKey={selectedInvitation}
        setSelectedAccessKey={setSelectedInvitation}
      />
      <DesktopHeader
        title={isSecondaryHostRestricted ? "" : labels.access_keys}
        isGuard={isGuard}
      />
      {isSecondaryHostRestricted ? (
        <DesktopBody hasFilters={false} filterData={[]}>
          <div className="w-100 h-100 px-8 pb-8 border-box flex align-items-center justify-content-center">
            <UserQrCode />
          </div>
        </DesktopBody>
      ) : (
        <DesktopBody hasFilters={true} filterData={filters.filterData}>
          <div className="w-100 h-100 px-8 pb-8 border-box">
            {isLoading ? (
              <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
                <CircularProgress />
              </div>
            ) : (
              <TableComponent
                selectableRow={true}
                data={tableDataToDisplay}
                onPressRow={(id: number) => {
                  const currentVerifiableId = verifiableIds.filter(
                    (verItem) => verItem?.accessKeyId === id
                  );
                  const selAccessKey = accessKeys?.filter(
                    (ak) => ak?.id === id
                  );
                  if (currentVerifiableId && currentVerifiableId.length > 0) {
                    setSelectedInvitationId(
                      currentVerifiableId[0]?.verifiableId
                    );
                  }
                  if (selAccessKey && selAccessKey.length > 0) {
                    setSelectedInvitation(selAccessKey[0]);
                  }
                  // setSelectedInvitationId(id);
                  setShowAccessKeyDetails(true);
                }}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                total={totalInvitations}
                tableHeight={"calc(100vh - 188px)"}
                emptyMessage={labels.no_invitations_registered}
              />
            )}
          </div>
        </DesktopBody>
      )}
      {!isSecondaryHostRestricted && (
        <DesktopDropView
          showView={showAccessKeyDetails}
          setShowView={setShowAccessKeyDetails}
          content={
            <div className="flex h-100 border-box" style={{ width: 500 }}>
              <div className="w-100 h-100 border-box p-8 scroll">
                <DesktopInvitationDetails invitationId={selectedInvitationId} />
              </div>
            </div>
          }
          extraFunc={selectedInvitation ? true : false}
          extraFuncElement={
            !isGuard && (
              <div className="flex align-items-center gap-8">
                <Tooltip title={labels.copy} arrow placement="top">
                  <div>
                    <IconifyIcon
                      icon={"fa-solid:copy"}
                      className={`${
                        selectedInvitation?.daleted_at
                          ? "disabled-color"
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? "pointer primary-color"
                          : "disabled-color"
                      }`}
                      style={{ fontSize: 20 }}
                      onClick={
                        selectedInvitation?.daleted_at
                          ? () => {}
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? copyToClipboard
                          : () => {}
                      }
                    />
                  </div>
                </Tooltip>
                <Tooltip title={labels.send_email} arrow placement="top">
                  <div>
                    <IconifyIcon
                      icon={"fa-solid:envelope"}
                      className={`${
                        selectedInvitation?.daleted_at
                          ? "disabled-color"
                          : (selectedInvitation?.one_time_visitor?.email ||
                              selectedInvitation?.verified_visitor?.email) &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? "primary-color pointer"
                          : "disabled-color"
                      }`}
                      style={{ fontSize: 20 }}
                      onClick={
                        selectedInvitation?.daleted_at
                          ? () => {}
                          : (selectedInvitation?.one_time_visitor?.email ||
                              selectedInvitation?.verified_visitor?.email) &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? sendEmail
                          : () => {}
                      }
                    />
                  </div>
                </Tooltip>
                <Tooltip title={labels.whatsapp} arrow placement="top">
                  <div>
                    <IconifyIcon
                      icon={"streamline:whatsapp-solid"}
                      className={`${
                        selectedInvitation?.daleted_at
                          ? "disabled-color"
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? selectedInvitation?.one_time_visitor
                            ? disabledWhatsAppByOneTimeVisitor
                              ? "disabled-color"
                              : "pointer primary-color"
                            : selectedInvitation?.verified_visitor
                            ? disabledWhatsAppByVerifiedVisitor
                              ? "disabled-color"
                              : "pointer primary-color"
                            : "disabled-color"
                          : "disabled-color"
                      }`}
                      style={{ fontSize: 20 }}
                      onClick={
                        selectedInvitation?.daleted_at
                          ? () => {}
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? selectedInvitation?.one_time_visitor
                            ? disabledWhatsAppByOneTimeVisitor
                              ? () => {}
                              : sendWhatsApp
                            : selectedInvitation?.verified_visitor
                            ? disabledWhatsAppByVerifiedVisitor
                              ? () => {}
                              : sendWhatsApp
                            : () => {}
                          : () => {}
                      }
                    />
                  </div>
                </Tooltip>
                <Tooltip title={labels.delete_key} arrow placement="top">
                  <div>
                    <IconifyIcon
                      icon={"fa7-solid:trash"}
                      className={`${
                        selectedInvitation?.daleted_at
                          ? "disabled-color"
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? "pointer primary-color"
                          : "disabled-color"
                      }`}
                      style={{ fontSize: 20 }}
                      onClick={
                        selectedInvitation?.daleted_at
                          ? () => {}
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? () => setOpenDeleteModal(true)
                          : () => {}
                      }
                    />
                  </div>
                </Tooltip>
                <Tooltip title="Editar" arrow placement="top">
                  <div>
                    <IconifyIcon
                      icon={"mdi:pencil"}
                      className={`${
                        selectedInvitation?.daleted_at
                          ? "disabled-color"
                          : selectedInvitation &&
                            selectedInvitation?.verifiable_ids[0]?.uuid
                          ? canEditSelectedAccessKey
                            ? "pointer primary-color"
                            : "disabled-color"
                          : "disabled-color"
                      }`}
                      style={{ fontSize: 20 }}
                      onClick={handleEditAccessKey}
                    />
                  </div>
                </Tooltip>
              </div>
            )
          }
          title={labels.invitation_details}
        />
      )}
      {!isSecondaryHostRestricted && !isGuard && (
        <DesktopDropView
          showView={newInvitation}
          setShowView={(show) => {
            setNewInvitation(show);
            if (!show) {
              // Limpiar el estado de edición cuando se cierra el sheet
              setEditingAccessKey(null);
            }
          }}
          content={
            <div className="flex h-100 border-box">
              <div className="h-100 border-box p-8 scroll">
                <NewInvitationForm
                  extraFunc={() => {
                    setNewInvitation(false);
                    setEditingAccessKey(null);
                    getAccessKeys(debouncedSearchFilter);
                  }}
                  openView={newInvitation}
                  showContacts={showContacts}
                  setShowContacts={setShowContacts}
                  isMobile={false}
                  editingAccessKey={editingAccessKey}
                />
              </div>
            </div>
          }
          extraFunc={true}
          extraFuncElement={
            <div className="flex align-items-center gap-6">
              <p className="t-nowrap body-text-md">Contactos</p>
              <IconifyIcon
                icon={showContacts ? "mdi:hide" : "mdi:show"}
                fontSize={24}
                className="primary-color pointer"
                onClick={() => setShowContacts(!showContacts)}
              />
            </div>
          }
          title={editingAccessKey ? labels.edit_invitation : labels.new_invitation}
        />
      )}
    </div>
  );
};

export default AccessKeys;
